/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { Ticket } from '@touchplan/common';
import { cssVar } from '@touchplan/css-var';
import {
	ITimelineTicketDisplayItem,
	timelineTicketDisplayItemFactory,
} from '@appPlanApplication/timeline/TimelineHelpers';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { takeUntil } from 'rxjs/operators';
import { GenericPool } from '../../../../app-data/models/data-types/GenericPool';
import { TimelineDisplayItem } from '@appPlanApplication/timeline/tickets/TimelineDisplayItem';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { Sprite, utils } from 'pixi.js-legacy';
import { TICKET_TEXTURE_OPTIONS } from '@appPlanApplication/tickets/TICKET_TEXTURE_OPTIONS';
import { TimelineTicketTap } from '@appPlanApplication/timeline/tickets/TimelineTicketTap';

const settings = {
	textColor: cssVar.white, // Color for text labels on grouped tickets
	textSize: 12, // Font size for count labels
	timelineHeight: cssVar.timelineHeight_px, // Height of the timeline area
	groupedColor: cssVar.timelineTicketGrey, // Background color for grouped ticket indicators
	xOffset: -20, // There is a 20px offset applied to the timeline tickets. No clue why, but copying that offset here as well.
	itemWidth: 20, // Copied from the old DOM CSS class. No clue why it is 20px wide.
	itemHeight: 21, // Copied from the old DOM CSS class. No clue why it is 21px tall.
};
/**
 * TimelineTicketDrawer is responsible for rendering tickets in the timeline view.
 * It handles both individual tickets and grouped tickets (when multiple tickets share the same finish date).
 * This class extends AbstractAreaDrawer and is designed to be controlled by the FancyTimeline.
 */
export class TimelineTicketDrawer extends AbstractAreaDrawer {
	/**
	 * A cache of timeline tickets, collected from the TimelineTicketService
	 * Used to actually draw the tickets in the timeline
	 * @private
	 */
	private _cachedTimelineTickets: Map<string, Ticket>;

	private _displayPool: GenericPool<TimelineDisplayItem<ITimelineTicketDisplayItem>> = new GenericPool<
		TimelineDisplayItem<ITimelineTicketDisplayItem>
	>(timelineTicketDisplayItemFactory, this._container);
	/**
	 * This stores dates we have already processed so we do not process and create duplicate objects on the same date.
	 * @private
	 */
	private _processedMap: Map<string, boolean> = new Map<string, boolean>();
	/**
	 * Create a new instance of the TimelineTicketDrawer. This Drawer is intended to be controlled by the FancyTimeline
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.UI_TIMELINE_TICKETS);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Main drawing method that renders all timeline tickets.
	 * Determines whether to render tickets individually or as grouped indicators based on finish date.
	 * @param timelineTickets Map of tickets to render, defaults to cached tickets if not provided
	 */
	public draw(timelineTickets: Map<string, Ticket> = this._cachedTimelineTickets): void {
		if (!timelineTickets || !timelineTickets.size) {
			this.hide();
			return;
		}

		this._displayPool.reset();
		this._processedMap.clear();

		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		timelineTickets.forEach((ticket: Ticket) => {
			if (this._processedMap.has(ticket.view.liveFinish)) {
				return;
			}

			const tickets: Ticket[] = this.getTicketsForDate(timelineTickets, ticket.view.liveFinish);
			this._processedMap.set(ticket.view.liveFinish, true);
			this.drawTicket(tickets);
		});
	}
	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(_?: number): void {
		if (!this._cachedTimelineTickets) {
			return;
		}

		const data: Map<string, Ticket> = this._cachedTimelineTickets;
		if (!data.size) {
			return;
		}
		super.update(0, cssVar.headerHeight_px);
	}
	/**
	 * Renders a timeline ticket on the timeline as a grouped visual (circle) or individual (milestone)
	 * Uses the ticket's role color for visual identification.
	 * @param tickets A list of tickets to render for the current date
	 */
	private drawTicket(tickets: Ticket[]): void {
		const count: number = tickets.length;
		const ticket: Ticket = tickets[0];
		const displayItem: TimelineDisplayItem<ITimelineTicketDisplayItem> = this._displayPool.grab();
		this.trySetupComponents(displayItem);

		const timelineX: number = ticket.view.isActive
			? ticket.timelineX
			: ticket.getPullColumnXFromDate(ticket.view.liveFinish);

		const sprite: Sprite = displayItem.sprite;
		sprite.anchor.set(0.5, 0.5);
		displayItem.setTexture(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_NAME]);
		displayItem.width = settings.itemWidth;
		displayItem.height = settings.itemHeight;
		displayItem.tint = ticket.view?.liveRole?.view?.color;
		displayItem.setData({ tickets });

		displayItem.position.set(
			this.parentApplication.camera.realToScreenX(timelineX + settings.xOffset),
			settings.timelineHeight / 2 + displayItem.height
		);

		// If this has a count, set the label config
		if (count > 1) {
			const label: HighResolutionText = displayItem.label;
			label.anchor.set(0.5, 0.5);
			label.position.set(sprite.x, sprite.y);
			displayItem.setLabel(count.toString());
			displayItem.setTexture(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_NAME]);
			displayItem.tint = settings.groupedColor;
		}
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.sourceOnly): void {
		super.destroy(options);
		this._displayPool.destroy(DestroyOptions.sourceOnly);
		this._processedMap.clear();
		this._processedMap = null;
		this._canvasAngularLand = null;
		this._cachedTimelineTickets = null;
		this._displayPool = null;
	}
	/**
	 * Gets all tickets for a specific date and returns them.
	 * This is used to connect a list of tickets with an individual TimelineTicketDisplayItem<T>
	 * @param timelineTickets The complete collection of timeline tickets
	 * @param searchDate The finish date to search for
	 * @returns Number of tickets that share the specified finish date
	 */
	private getTicketsForDate(timelineTickets: Map<string, Ticket>, searchDate: string): Ticket[] {
		let tickets: Ticket[] = [];
		timelineTickets.forEach((ticket) => {
			if (ticket.view.liveFinish === searchDate) {
				tickets.push(ticket);
			}
		});
		return tickets;
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.timelineTicketService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((tickets: Map<string, Ticket>) => {
				this._cachedTimelineTickets = tickets;
				this.draw(tickets);
			});

		this.parentApplication.camera.changed$.pipe(takeUntil(this._shutdownSubject$)).subscribe((_) => {
			this.update();
			this.draw();
		});
	}

	/**
	 * Attempts to connect the `TimelineTicketTap` gesture to the provided pooled TimelineDisplayItem
	 * If there is already a component attached, it will silently return and do nothing.
	 * @param item
	 * @private
	 */
	private trySetupComponents(item: TimelineDisplayItem<ITimelineTicketDisplayItem>): void {
		if (item.components.count) {
			return;
		}

		item.components.add(`timelineTicketTap`, new TimelineTicketTap(), this._canvasAngularLand, item);
	}
}
